// src/pages/ConfirmBookingPage.jsx
//Lo que sale despues de pagar
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, Box, Typography, Paper, Button, 
  CircularProgress, Alert, Divider 
} from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
import axiosInstance from '../axiosInstance';

const ConfirmBookingPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookingData, setBookingData] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/api/appointments/verify-booking/${token}/`);
        setBookingData(response.data);
      } catch (err) {
        console.error('Error al verificar reserva:', err);
        setError(err.response?.data?.error || 'El enlace de confirmación no es válido o ha expirado.');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      verifyToken();
    }
  }, [token]);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await axiosInstance.post(`/api/appointments/confirm-booking/${token}/`);
      setConfirmed(true);
    } catch (err) {
      console.error('Error al confirmar reserva:', err);
      setError(err.response?.data?.error || 'Error al confirmar la cita. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Verificando reserva...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Error color="error" sx={{ fontSize: 60 }} />
            <Typography variant="h5" color="error" gutterBottom sx={{ mt: 2 }}>
              Error de Verificación
            </Typography>
            <Typography variant="body1" align="center" paragraph>
              {error}
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/doctors')}
              sx={{ mt: 2 }}
            >
              Buscar Médicos
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (confirmed) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CheckCircle color="success" sx={{ fontSize: 60 }} />
            <Typography variant="h5" color="success.main" gutterBottom sx={{ mt: 2 }}>
              ¡Cita Confirmada!
            </Typography>
            <Typography variant="body1" align="center" paragraph>
              Tu cita ha sido confirmada exitosamente. Recibirás un correo con los detalles.
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/patient/register')}
              sx={{ mt: 2 }}
            >
              Crear una cuenta para gestionar tus citas
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Confirmar Cita Médica
        </Typography>
        
        <Alert severity="info" sx={{ my: 2 }}>
          Por favor, revisa los detalles de tu cita antes de confirmar.
        </Alert>
        
        {bookingData && (
          <>
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Detalles del Médico
              </Typography>
              <Typography variant="body1">
                Dr. {bookingData.doctor_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {bookingData.specialty}
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Detalles de la Cita
              </Typography>
              <Typography variant="body1">
                Fecha: {new Date(bookingData.appointment_date).toLocaleDateString()}
              </Typography>
              <Typography variant="body1">
                Hora: {new Date(bookingData.appointment_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
              {bookingData.reason && (
                <Typography variant="body1">
                  Motivo: {bookingData.reason}
                </Typography>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Tus Datos
              </Typography>
              <Typography variant="body1">
                Nombre: {bookingData.patient_name}
              </Typography>
              <Typography variant="body1">
                Email: {bookingData.patient_email}
              </Typography>
              <Typography variant="body1">
                Teléfono: {bookingData.patient_phone}
              </Typography>
            </Box>
          </>
        )}
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/doctors')}
          >
            Cancelar
          </Button>
          
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleConfirm}
            disabled={loading}
          >
            Confirmar Cita
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ConfirmBookingPage;

